import { TOA_GetFertilizationsForFreezing } from '@/rtk-query.gql';

import { LiveEmbryoForPrint, TempEmbryoForPrint } from './types';

export function processEmbryosForPrint(
  tempEmbryos: TempEmbryoForPrint[],
  liveEmbryos: LiveEmbryoForPrint[],
  ferts: TOA_GetFertilizationsForFreezing[],
  filename: string,
  recoveryCode: string | undefined,
) {
  if (filename === 'CaneTabLabel' || filename === 'GobletLabel') {
    const uniqueCanes = new Set([
      ...tempEmbryos.map((emb) => emb.cane),
      ...liveEmbryos.map((emb) => emb.cane),
    ]);

    const fertilizationMap = new Map<string | number, number>();
    const selectedFertIds = new Set(
      [
        ...tempEmbryos.map((emb) => emb.fertilizationId),
        ...liveEmbryos.map((emb) => emb.fertilizationId),
      ].filter((id): id is string => id !== null),
    );

    const selectedFertilizations = ferts.filter((fert) =>
      selectedFertIds.has(fert.fertId as string),
    );

    for (const fert of selectedFertilizations) {
      fertilizationMap.set(fert.fertId as string, fert.plateNumber || 0);
    }

    const items = Array.from(uniqueCanes)
      .map((cane) => {
        const tempEmb = tempEmbryos.find((e) => e.cane === cane);
        const liveEmb = liveEmbryos.find((e) => e.cane === cane);
        const emb = tempEmb || liveEmb;

        return {
          fertilizationId: emb?.fertilizationId,
          cane,
          recoveryCode,
          ...(tempEmb && { intEmbryoId: tempEmb.embryoFreezeEntryId }),
          ...(liveEmb && { embryoId: liveEmb.id }),
        };
      })
      .sort((a, b) => {
        const plateA = a.fertilizationId
          ? (fertilizationMap.get(a.fertilizationId) ?? 0)
          : 0;
        const plateB = b.fertilizationId
          ? (fertilizationMap.get(b.fertilizationId) ?? 0)
          : 0;
        return plateA - plateB;
      });

    return items;
  }

  const fertilizationMap = new Map<string | number, number>();
  const selectedFertIds = new Set(
    [
      ...tempEmbryos.map((emb) => emb.fertilizationId),
      ...liveEmbryos.map((emb) => emb.fertilizationId),
    ].filter((id): id is string => id !== null),
  );

  const selectedFertilizations = ferts.filter((fert) =>
    selectedFertIds.has(fert.fertId as string),
  );

  for (const fert of selectedFertilizations) {
    fertilizationMap.set(fert.fertId as string, fert.plateNumber || 0);
  }

  const sortByPlateDonorStraw = (a: any, b: any) => {
    const plateA = a.fertilizationId
      ? (fertilizationMap.get(a.fertilizationId) ?? 0)
      : 0;
    const plateB = b.fertilizationId
      ? (fertilizationMap.get(b.fertilizationId) ?? 0)
      : 0;

    if (plateA !== plateB) {
      return plateA - plateB;
    }
    const nameA = (a.donorName ?? '').toString();
    const nameB = (b.donorName ?? '').toString();
    const nameCmp = nameA.localeCompare(nameB, undefined, {
      sensitivity: 'base',
    });
    if (nameCmp !== 0) return nameCmp;

    const strawA = parseInt(a.strawLabel) || 0;
    const strawB = parseInt(b.strawLabel) || 0;
    return strawA - strawB;
  };

  const allEmbryos = [
    ...tempEmbryos.map((emb) => ({
      fertilizationId: emb.fertilizationId,
      cane: emb.cane,
      straw: emb.straw?.toString(),
      embryoId: emb.embryoId,
      intEmbryoId: emb.embryoFreezeEntryId,
      recoveryCode,
      freezeMethod: emb.freezeMethodName,
      donorName: emb.donorName,
      isTemp: true,
    })),
    ...liveEmbryos.map((emb) => {
      const freezeMethod = emb.freezeMethodName || emb.freezeMethod?.name;

      const straw = emb.strawNumber?.toString() ?? emb.strawLabel?.toString();

      return {
        fertilizationId: emb.fertilizationId,
        cane: emb.cane,
        straw,
        embryoId: emb.id,
        recoveryCode,
        freezeMethod,
        donorName: emb.donorName,
        isTemp: false,
      };
    }),
  ].sort(sortByPlateDonorStraw);

  const hasVitrification = allEmbryos.some(
    (emb) => emb.freezeMethod?.toLowerCase() === 'vitrification',
  );

  if (hasVitrification) {
    const embryosByFert = allEmbryos.reduce(
      (acc, embryo) => {
        const fertId = embryo.fertilizationId ?? 'null';
        if (!acc[fertId]) {
          acc[fertId] = { temp: [], live: [] };
        }
        if (embryo.isTemp) {
          acc[fertId].temp.push(embryo);
        } else {
          acc[fertId].live.push(embryo);
        }
        return acc;
      },
      {} as Record<string, { temp: any[]; live: any[] }>,
    );

    const items = Object.entries(embryosByFert)
      .sort(([fertIdA], [fertIdB]) => {
        const plateA = fertilizationMap.get(fertIdA) ?? 0;
        const plateB = fertilizationMap.get(fertIdB) ?? 0;
        return plateA - plateB;
      })
      .flatMap(([_fertId, { temp, live }]) => {
        const processLiveByStraw = () => {
          if (
            live.length === 0 ||
            live[0]?.freezeMethod?.toLowerCase() !== 'vitrification'
          ) {
            return live;
          }

          const strawGroups = live.reduce<Record<string, typeof live>>(
            (acc, embryo) => {
              const straw = embryo.straw?.toString() || '';
              if (!acc[straw]) acc[straw] = [];
              acc[straw].push(embryo);
              return acc;
            },
            {},
          );
          return Object.values(strawGroups).map((group) => group[0]);
        };

        const processTempByStraw = () => {
          if (
            temp.length === 0 ||
            temp[0]?.freezeMethod?.toLowerCase() !== 'vitrification'
          ) {
            return temp;
          }

          const strawGroups = temp.reduce<Record<string, typeof temp>>(
            (acc, embryo) => {
              const straw = embryo.straw?.toString() || '';
              if (!acc[straw]) acc[straw] = [];
              acc[straw].push(embryo);
              return acc;
            },
            {},
          );
          return Object.values(strawGroups).map((group) => group[0]);
        };

        return [...processLiveByStraw(), ...processTempByStraw()];
      });

    return items.map(({ donorName, ...rest }) => rest);
  }

  return allEmbryos.map(({ donorName, ...rest }) => rest);
}
